import {
  selOrganizationMemberships,
  stOrganizationModule
} from '@features/st-organizations/st-organization-module'
import { OrganizationSettingsScaffold } from '@features/st-organizations/st-organization-settings-scaffold'
import { useProcess, useSelect } from '~/store'
import { OrganizationMembership, OrganizationRole } from '@st/sdk'
import {
  Button,
  ContextMenu,
  ContextMenuItem,
  Dialog,
  DialogButtons,
  Modal,
  PlusIcon,
  Select,
  Table,
  TagChip,
  TextInput
} from '@st/theme'
import { isValidEmail } from '@st/util/email'
import { useState } from 'react'
import { match } from '@st/pattern'
import { useCurrentUser } from '~/features/app-hooks'
import { selOrganizationId } from '@features/st-organizations/st-organization-module'

type OrganizationTeamAction =
  | { type: 'add' }
  | { type: 'confirmRemove'; membership: OrganizationMembership }
  | { type: 'resendInvite'; membership: OrganizationMembership }

export function OrganizationTeamPage() {
  const user = useCurrentUser()
  const stOrganization = useProcess('stOrganization')
  const memberships = useSelect('stOrganization', selOrganizationMemberships)

  const currentUserRole = memberships.find((m) => m.userId == user?.id)

  const [action, setAction] = useState<OrganizationTeamAction | undefined>()

  function onAction(action: OrganizationTeamAction) {
    switch (action.type) {
      case 'add':
      case 'confirmRemove':
        setAction(action)
        break
      case 'resendInvite':
        stOrganization.send({
          type: 'resendInvite',
          userId: action.membership.userId,
          email: action.membership.user.email
        })
        break
    }
  }

  return (
    <OrganizationSettingsScaffold title="Settings">
      <h2 className="mb-5 text-xl text-gray-800">Team</h2>

      <Table>
        <Table.Head>
          <Table.Th>Email</Table.Th>
          <Table.Th className="w-60">Role</Table.Th>
          <Table.Th className="w-32"></Table.Th>
        </Table.Head>
        <Table.Body>
          {memberships.map((membership) => (
            <Table.Row key={membership.userId}>
              <Table.Cell>{membership.user.email}</Table.Cell>
              <Table.Cell>
                <TagChip color="gray" className="w-20">
                  {membership.role!}
                </TagChip>
              </Table.Cell>
              <Table.Cell>
                <div className="flex flex-row items-center">
                  <ContextMenu>
                    <ContextMenuItem
                      onClick={() => onAction({ type: 'resendInvite', membership: membership })}
                    >
                      Resend invitation email
                    </ContextMenuItem>
                    <ContextMenuItem
                      onClick={() => onAction({ type: 'confirmRemove', membership: membership })}
                    >
                      Remove from team
                    </ContextMenuItem>
                  </ContextMenu>
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Button
        variant="primary"
        className="mt-12 self-end"
        leadingIcon={<PlusIcon className="h-4 w-4" />}
        onClick={() => setAction({ type: 'add' })}
      >
        Add team member
      </Button>

      {match(action)
        .with({ type: 'add' }, () => (
          <Modal isOpen={true}>
            <AddTeamMemberDialog
              hasPermission={currentUserRole?.role == 'owner'}
              onClose={() => setAction(undefined)}
            />
          </Modal>
        ))
        .with({ type: 'confirmRemove' }, ({ membership }) => {
          return (
            <Modal isOpen={true}>
              <RemoveTeamMemberDialog
                hasPermission={currentUserRole?.role == 'owner'}
                membership={membership}
                onClose={() => setAction(undefined)}
              />
            </Modal>
          )
        })
        .otherwise(() => null)}
    </OrganizationSettingsScaffold>
  )
}

function AddTeamMemberDialog({
  hasPermission,
  onClose
}: {
  hasPermission: boolean
  onClose: () => void
}) {
  const stOrganization = useProcess('stOrganization')

  const organizationId = useSelect('stOrganization', selOrganizationId)

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<OrganizationRole>('member')

  function onPressInvite() {
    stOrganization.send({
      type: 'addMemberByEmailAndSendInvite',
      email,
      role: role as OrganizationRole
    })
    onClose()
  }

  if (!hasPermission) {
    return (
      <Dialog
        title="Unauthorized"
        subtitle="Only the owner of an organization can add a member to the team"
        buttons={
          <DialogButtons>
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </DialogButtons>
        }
      />
    )
  }

  return (
    <Dialog
      title="Add team member"
      subtitle="Enter the email of the team member you would like to invite"
      buttons={
        <DialogButtons>
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" disabled={!isValidEmail(email)} onClick={onPressInvite}>
            Invite
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-row gap-2">
          <TextInput
            className="w-full"
            placeholder="Email address"
            autoFocus={true}
            value={email}
            onChange={setEmail}
          />
          <Select
            className="w-48"
            value={role}
            onChange={(value) => setRole(value as OrganizationRole)}
            options={[
              { value: 'member', label: 'Member' },
              { value: 'restricted', label: 'Restricted' }
            ]}
            buildValue={(opt) => opt.value as OrganizationRole}
            buildLabel={(opt) => opt.label}
          />
        </div>
        <div className="text-base text-gray-500">
          {match(role)
            .with('restricted', () => {
              return "Restricted team members can only access binders they've been assigned to."
            })
            .otherwise(() => null)}
        </div>
      </div>
    </Dialog>
  )
}

function RemoveTeamMemberDialog({
  hasPermission,
  membership,
  onClose
}: {
  hasPermission: boolean
  membership: OrganizationMembership
  onClose: () => void
}) {
  const stOrganization = useProcess('stOrganization')

  function onClickRemove() {
    stOrganization.send({
      type: 'removeMember',
      userId: membership.userId,
      email: membership.user.email
    })
    onClose()
  }

  if (membership.role == 'owner') {
    return (
      <Dialog
        title="Unauthorized"
        subtitle="It is not possible to remove the owner of an organization"
        buttons={
          <DialogButtons>
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </DialogButtons>
        }
      />
    )
  } else if (!hasPermission) {
    return (
      <Dialog
        title="Unauthorized"
        subtitle="Only the owner of an organization can remove a member"
        buttons={
          <DialogButtons>
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </DialogButtons>
        }
      />
    )
  }

  return (
    <Dialog
      title="Remove from team"
      subtitle={`Please confirm that you would like to remove ${membership.user.email} from the organization`}
      buttons={
        <DialogButtons>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onClickRemove}>
            Remove
          </Button>
        </DialogButtons>
      }
    />
  )
}
